import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";

export default function VolunteerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = () => api.get(`/api/volunteers/${id}/hours`).then((r) => setData(r.data));
  useEffect(() => {
    let cancelled = false;
    api.get(`/api/volunteers/${id}/hours`).then((r) => { if (!cancelled) setData(r.data); });
    return () => { cancelled = true; };
  }, [id]);

  const logHours = async () => {
    try {
      setBusy(true);
      await api.post(`/api/volunteers/${id}/hours`, { hours: Number(hours), note });
      toast({ title: "Hours logged" });
      setHours(""); setNote("");
      load();
    } catch (e) {
      toast({ title: "Failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setBusy(false); }
  };

  if (!data) return <PageBody><div className="text-sm text-muted-foreground">Loading…</div></PageBody>;
  const v = data.volunteer;

  return (
    <>
      <PageHeader title={v.full_name} subtitle={v.email || "Volunteer profile"}>
        <Link to="/app/volunteers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="volunteer-back">
          <ArrowLeft size={14} /> Back to volunteers
        </Link>
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Hours logged</div>
            <div className="serif text-4xl text-primary mt-2">{Number(v.hours_total || 0).toFixed(0)}</div>
            <div className="text-xs text-muted-foreground mt-1">Across all programs</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</div>
            <div className="mt-2"><span className={`text-xs px-2 py-1 rounded ${v.active ? "bg-emerald-100 text-emerald-800" : "bg-accent text-muted-foreground"}`}>{v.active ? "Active" : "Inactive"}</span></div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Skills</div>
            <div className="mt-2 flex flex-wrap gap-1">{(v.skills || []).map((s) => <span key={s} className="text-xs px-2 py-0.5 rounded bg-accent text-muted-foreground">{s}</span>)}</div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 mb-6">
          <h3 className="font-medium text-sm mb-3">Log hours</h3>
          <div className="flex gap-3 text-sm">
            <input type="number" placeholder="Hours" value={hours} onChange={(e) => setHours(e.target.value)} className="px-3 py-2 rounded border border-border bg-background w-24" data-testid="vol-detail-hours" />
            <input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="px-3 py-2 rounded border border-border bg-background flex-1" />
            <button onClick={logHours} disabled={busy || !hours} className="inline-flex items-center gap-1 px-3 py-2 rounded bg-primary text-primary-foreground text-sm" data-testid="vol-detail-log">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Log
            </button>
          </div>
        </div>

        <section className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border"><h3 className="font-medium text-sm">History ({data.entries.length})</h3></div>
          {data.entries.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">No hours logged yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-accent/30">
                <tr><th className="text-left px-4 py-2">Date</th><th className="text-right px-4 py-2">Hours</th><th className="text-left px-4 py-2">Note</th></tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-border/40" data-testid={`vol-hours-${e.id}`}>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground tabular-nums">{e.logged_on}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{e.hours}</td>
                    <td className="px-4 py-2.5 text-xs">{e.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {data.by_program?.length > 0 && (
          <section className="rounded-xl border border-border bg-card mt-6 p-5" data-testid="vol-by-program">
            <h3 className="font-medium text-sm mb-3">Hours by program</h3>
            <ResponsiveContainer width="100%" height={Math.max(180, data.by_program.length * 38)}>
              <BarChart data={data.by_program} layout="vertical" margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis dataKey="program" type="category" width={140} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => [`${v} hrs`, "Hours"]} />
                <Bar dataKey="hours" fill="hsl(207 70% 45%)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </section>
        )}
      </PageBody>
    </>
  );
}
